import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Coffee, Clock, Repeat, Store, Truck, ChevronRight, Heart, Star, Trash2 } from "lucide-react";

const statusLabel = {
  pending: "Awaiting confirmation",
  confirmed: "Confirmed",
  brewing: "Brewing",
  ready: "Ready",
  completed: "Delivered / Picked up",
  cancelled: "Cancelled",
};
const statusColor = {
  pending: "#D69F4C",
  confirmed: "#7A8B76",
  brewing: "#C05A46",
  ready: "#7A8B76",
  completed: "#1A1514",
  cancelled: "#7B7573",
};

function currency(n) { return `$${Number(n || 0).toFixed(2)}`; }

function itemsSummary(items) {
  return items.map((line) =>
    `${line.quantity}× ${line.drink.name} ${line.size}`
  ).join(" · ");
}

function OrderRow({ order, onReorder, onSaveFavorite }) {
  const isDelivery = order.fulfillment_type === "delivery";
  return (
    <li
      data-testid={`account-order-${order.order_number}`}
      className="flex flex-col md:flex-row md:items-center gap-3 border border-black/10 rounded-lg p-4 bg-white"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-jet text-xs text-[#C05A46]">{order.order_number}</span>
          <span
            className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: statusColor[order.status] || "#4A4341" }}
          >
            {statusLabel[order.status]}
          </span>
          <span className="text-xs text-[#7B7573] inline-flex items-center gap-1">
            {isDelivery ? <Truck className="h-3 w-3" /> : <Store className="h-3 w-3" />}
            {isDelivery ? (order.delivery_group || "Delivery") : "Pickup"}
            · {order.pickup_date} · {order.pickup_time}
          </span>
        </div>
        <p className="mt-1 text-sm text-[#1A1514]">{itemsSummary(order.items)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <span className="font-mono-jet text-sm">{currency(order.total)}</span>
        <button
          type="button"
          onClick={() => onSaveFavorite(order)}
          data-testid={`account-save-fav-${order.order_number}`}
          title="Save as favorite"
          className="rounded-full border border-black/15 h-8 w-8 inline-flex items-center justify-center text-[#7B7573] hover:text-[#C05A46] hover:border-[#C05A46] transition-colors"
        >
          <Heart className="h-3.5 w-3.5" />
        </button>
        <Link
          to={`/order/success/${order.order_number}`}
          data-testid={`account-view-${order.order_number}`}
          className="rounded-full border border-black/15 px-3 py-1.5 text-xs font-medium hover:border-[#1A1514] transition-colors inline-flex items-center gap-1"
        >
          View <ChevronRight className="h-3 w-3" />
        </Link>
        <Button
          type="button"
          size="sm"
          data-testid={`account-reorder-${order.order_number}`}
          onClick={() => onReorder(order.items, `order ${order.order_number}`)}
          className="btn-brand rounded-full h-8 px-3 text-xs inline-flex items-center gap-1"
        >
          <Repeat className="h-3 w-3" /> Reorder
        </Button>
      </div>
    </li>
  );
}

function FavoriteRow({ fav, onReorder, onDelete }) {
  return (
    <li
      data-testid={`account-favorite-${fav.id}`}
      className="flex flex-col md:flex-row md:items-center gap-3 border border-[#D69F4C]/40 bg-[#D69F4C]/5 rounded-lg p-4"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Star className="h-3.5 w-3.5 text-[#D69F4C]" fill="#D69F4C" strokeWidth={0} />
          <span data-testid={`favorite-name-${fav.id}`} className="font-serif-display text-lg leading-tight">
            {fav.name}
          </span>
        </div>
        <p className="mt-1 text-sm text-[#1A1514]">{itemsSummary(fav.items)}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono-jet text-sm">{currency(fav.total)}</span>
        <button
          type="button"
          onClick={() => onDelete(fav)}
          data-testid={`favorite-delete-${fav.id}`}
          title="Remove favorite"
          className="rounded-full border border-black/15 h-8 w-8 inline-flex items-center justify-center text-[#7B7573] hover:text-[#C05A46] hover:border-[#C05A46] transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <Button
          type="button"
          size="sm"
          data-testid={`favorite-reorder-${fav.id}`}
          onClick={() => onReorder(fav.items, fav.name)}
          className="btn-brand rounded-full h-8 px-3 text-xs inline-flex items-center gap-1"
        >
          <Repeat className="h-3 w-3" /> Order
        </Button>
      </div>
    </li>
  );
}

export default function CustomerAccount() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveTarget, setSaveTarget] = useState(null); // { items, defaultName }
  const [favName, setFavName] = useState("");
  const [savingFav, setSavingFav] = useState(false);
  const [confirmDeleteFav, setConfirmDeleteFav] = useState(null);

  const load = async () => {
    try {
      const [{ data: ordersData }, { data: favsData }] = await Promise.all([
        api.get("/customers/me/orders"),
        api.get("/customers/me/favorites"),
      ]);
      setOrders(ordersData);
      setFavorites(favsData);
    } catch (e) {
      console.debug("Failed to load account data", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "customer") {
      load();
      const iv = setInterval(load, 15000);
      return () => clearInterval(iv);
    }
  }, [user]);

  if (user === null) return null;
  if (!user || user.role !== "customer") return <Navigate to="/signin" replace />;

  const activeOrder = orders.find(
    (o) => !["completed", "cancelled"].includes(o.status)
  );
  const pastOrders = orders.filter(
    (o) => ["completed", "cancelled"].includes(o.status)
  );

  const reorder = (items, from) => {
    navigate("/order", { state: { prefillCart: items, fromOrder: from } });
  };

  const openSaveFavorite = (order) => {
    setSaveTarget({ items: order.items, defaultName: `Order ${order.order_number}` });
    setFavName(`Order ${order.order_number}`);
  };

  const saveFavorite = async () => {
    if (!saveTarget) return;
    const name = favName.trim();
    if (!name) return toast.error("Give it a name first");
    setSavingFav(true);
    try {
      const { data } = await api.post("/customers/me/favorites", {
        name,
        items: saveTarget.items,
      });
      setFavorites((f) => [data, ...f]);
      toast.success(`Saved "${data.name}" to your favorites`);
      setSaveTarget(null);
      setFavName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save favorite");
    } finally {
      setSavingFav(false);
    }
  };

  const deleteFavorite = async () => {
    if (!confirmDeleteFav) return;
    const fav = confirmDeleteFav;
    try {
      await api.delete(`/customers/me/favorites/${fav.id}`);
      setFavorites((f) => f.filter((x) => x.id !== fav.id));
      toast.success(`Removed "${fav.name}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove favorite");
    } finally {
      setConfirmDeleteFav(null);
    }
  };

  return (
    <div data-testid="customer-account-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="label-eyebrow">Your account</p>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
              Hi, {user.name?.split(" ")[0]}.
            </h1>
            <p className="mt-2 text-sm text-[#4A4341]">
              {user.email} · <span className="font-mono-jet">{user.phone}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/order"
              data-testid="account-new-order-btn"
              className="btn-brand rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
            >
              <Coffee className="h-4 w-4" /> New order
            </Link>
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="account-signout-btn"
              className="text-[#4A4341] hover:text-[#C05A46]"
            >
              Sign out
            </Button>
          </div>
        </div>

        {/* Active order tracker */}
        <section className="mt-10">
          <p className="label-eyebrow text-[#1A1514]">Latest order in progress</p>
          {activeOrder ? (
            <Link
              to={`/order/success/${activeOrder.order_number}`}
              data-testid="account-active-order-card"
              className="mt-3 block rounded-2xl border border-[#C05A46]/40 bg-[#C05A46]/6 p-6 hover:border-[#C05A46] transition-colors"
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-mono-jet text-xs text-[#C05A46]">{activeOrder.order_number}</p>
                  <p className="mt-1 font-serif-display text-2xl inline-flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: statusColor[activeOrder.status] }}
                    />
                    {statusLabel[activeOrder.status]}
                  </p>
                  <p className="mt-1 text-sm text-[#4A4341] inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> {activeOrder.pickup_date} · {activeOrder.pickup_time}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#1A1514]">{itemsSummary(activeOrder.items)}</p>
                  <p className="mt-1 font-serif-display text-xl">{currency(activeOrder.total)}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-[#C05A46] font-semibold">Tap to see live status →</p>
            </Link>
          ) : (
            <div data-testid="account-no-active-order" className="mt-3 rounded-2xl border border-black/10 bg-white p-6 text-center">
              <p className="font-serif-display text-lg">No order in progress right now.</p>
              <p className="mt-1 text-sm text-[#7B7573]">Craft a new one and I&rsquo;ll get brewing.</p>
              <Link
                to="/order"
                data-testid="account-start-order-btn"
                className="mt-4 inline-flex items-center gap-2 btn-brand rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                <Coffee className="h-4 w-4" /> Start an order
              </Link>
            </div>
          )}
        </section>

        {/* Favorites */}
        <section className="mt-12">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="label-eyebrow text-[#1A1514]">Saved favorites</p>
            <p className="text-[11px] text-[#7B7573]">1-tap reorder your regulars</p>
          </div>
          {favorites.length === 0 ? (
            <div data-testid="account-empty-favorites" className="mt-3 rounded-2xl border border-dashed border-black/15 bg-white/60 p-6 text-center">
              <Heart className="mx-auto h-6 w-6 text-[#D69F4C]" strokeWidth={1.5} />
              <p className="mt-2 font-serif-display text-lg">No favorites yet.</p>
              <p className="mt-1 text-sm text-[#7B7573]">
                Tap the heart on any past order below to save it — then reorder in one tap.
              </p>
            </div>
          ) : (
            <ul data-testid="account-favorites-list" className="mt-4 space-y-3">
              {favorites.map((f) => (
                <FavoriteRow
                  key={f.id}
                  fav={f}
                  onReorder={reorder}
                  onDelete={(fav) => setConfirmDeleteFav(fav)}
                />
              ))}
            </ul>
          )}
        </section>

        {/* History */}
        <section className="mt-12">
          <p className="label-eyebrow text-[#1A1514]">Your order history</p>
          {loading ? (
            <p className="mt-4 text-sm text-[#7B7573]">Loading…</p>
          ) : orders.length === 0 ? (
            <p data-testid="account-empty-history" className="mt-4 text-sm text-[#7B7573]">
              You haven&rsquo;t placed any orders yet. Once you do, they&rsquo;ll show up here for easy reorder.
            </p>
          ) : (
            <ul className="mt-4 space-y-3">
              {orders.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  onReorder={reorder}
                  onSaveFavorite={openSaveFavorite}
                />
              ))}
            </ul>
          )}
          {pastOrders.length > 0 ? (
            <p className="mt-4 text-[11px] text-[#7B7573]">
              Showing your most recent {orders.length} orders.
            </p>
          ) : null}
        </section>
      </main>

      {/* Save Favorite dialog */}
      <AlertDialog open={!!saveTarget} onOpenChange={(open) => { if (!open) setSaveTarget(null); }}>
        <AlertDialogContent data-testid="save-favorite-dialog" className="bg-[#F5F5F0] border-black/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl tracking-tight">
              Save as favorite
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A4341]">
              Give this drink combo a name so you can reorder it in one tap later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2">
            <Label htmlFor="fav-name">Favorite name</Label>
            <Input
              id="fav-name"
              data-testid="save-favorite-name-input"
              value={favName}
              onChange={(e) => setFavName(e.target.value)}
              maxLength={60}
              placeholder="Morning latte, Sunday church order…"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="save-favorite-cancel" disabled={savingFav}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="save-favorite-confirm"
              disabled={savingFav || !favName.trim()}
              onClick={(e) => { e.preventDefault(); saveFavorite(); }}
              className="btn-brand rounded-full"
            >
              {savingFav ? "Saving…" : "Save favorite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Favorite confirm */}
      <AlertDialog open={!!confirmDeleteFav} onOpenChange={(open) => { if (!open) setConfirmDeleteFav(null); }}>
        <AlertDialogContent data-testid="delete-favorite-dialog" className="bg-[#F5F5F0] border-black/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl tracking-tight">
              Remove this favorite?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A4341]">
              {confirmDeleteFav ? `"${confirmDeleteFav.name}" will be removed from your saved favorites.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-favorite-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-favorite-confirm"
              onClick={(e) => { e.preventDefault(); deleteFavorite(); }}
              className="rounded-full bg-[#C05A46] hover:bg-[#A84C3A]"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
